// src/pages/DashboardPage.js
// แดชบอร์ดภาพรวม ปรับตามข้อกำหนดใหม่ (สรุปยอด, กราฟเลือกช่วง, distribution)

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useNotification } from "../NotificationContext";
import OrderDetailModal from "../components/OrderDetailModal";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const DashboardPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  // Summary
  const [summary, setSummary] = useState(null);
  const [monthExpenses, setMonthExpenses] = useState(0);
  const [loading, setLoading] = useState(false);
  const [shopName, setShopName] = useState("POS88"); // default

  // Orders draft list
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [unpaidOrdersCount, setUnpaidOrdersCount] = useState(0);

  // Sales chart months selector (3/6/12)
  const [salesMonths, setSalesMonths] = useState(6);

  // Peak hours & weekday months selector (3/6/12)
  const [peakMonths, setPeakMonths] = useState(6);
  const [weekdayMonths, setWeekdayMonths] = useState(6); // ใช้ fetch เดียวกับ peak

  // Monthday distribution
  const now = new Date();
  const [monthdayMode, setMonthdayMode] = useState('range'); // 'range' or 'month'
  const [monthdayMonths, setMonthdayMonths] = useState(6); // 3, 6, 12
  const [monthdayYear, setMonthdayYear] = useState(now.getFullYear());
  const [monthdayMonth, setMonthdayMonth] = useState(now.getMonth() + 1); // 1-12

  // Top items (3/6/12) - ปรับตาม requirement (ตัด 1 เดือนออก)
  const [popularMonths, setPopularMonths] = useState(3);

  // Data sets
  const [monthlySales, setMonthlySales] = useState([]); // [{ label, sales, expenses }]
  const [peakHours, setPeakHours] = useState([]);
  const [ordersByWeekday, setOrdersByWeekday] = useState([]);
  const [ordersByMonthday, setOrdersByMonthday] = useState([]);
  const [topServices, setTopServices] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topLoading, setTopLoading] = useState(false);

  // Order detail modal (still reused by unpaid draft table)
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [loadingOrderDetail, setLoadingOrderDetail] = useState(false);

  const formatMoney = (val) =>
    (Number(val) || 0).toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const toIsoDate = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Load summary + unpaid orders
  const loadSummaryAndDrafts = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

      // โหลด KPI สรุปหลักก่อน
      const summaryRes = await api.get("/dashboard/summary");
      setSummary(summaryRes.data || {});

      // โหลดรายการค้างชำระ
      const unpaidRes = await api.get("/orders", { params: { status: "draft" } });
      const drafts = unpaidRes.data || [];
      setUnpaidOrders(drafts);
      setUnpaidOrdersCount(drafts.length);

      // โหลดค่าใช้จ่ายประจำเดือน
      const expensesRes = await api.get("/expenses", {
        params: { date_from: toIsoDate(monthStart), date_to: toIsoDate(today) }
      });
      const expRows = Array.isArray(expensesRes.data) ? expensesRes.data : [];
      const expTotal = expRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      setMonthExpenses(expTotal);
    } catch (err) {
      console.error(err);
      addNotification(
        err.response?.data?.message || "โหลดข้อมูลสรุปไม่สำเร็จ",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  // Load shop name from settings
  const loadShopName = async () => {
    try {
      const res = await api.get("/settings");
      const data = res.data || {};
      if (data.shop_name) {
        setShopName(data.shop_name);
      }
    } catch (err) {
      console.error("Failed to load shop name:", err);
      // ไม่แสดง notification เพราะไม่ critical
    }
  };

  // Load shop name on mount
  useEffect(() => {
    loadShopName();
  }, []);

  // Load monthly sales aggregated by month for selected salesMonths
  const loadSalesChart = async () => {
    try {
      const today = new Date();
      const start = new Date(
        today.getFullYear(),
        today.getMonth() - (salesMonths - 1),
        1
      );
      const params = { params: { date_from: toIsoDate(start), date_to: toIsoDate(today) } };
      const [salesRes, expensesRes] = await Promise.all([
        api.get("/reports/sales", params),
        api.get("/expenses", params),
      ]);
      const sales = salesRes.data || [];
      const expenses = Array.isArray(expensesRes.data) ? expensesRes.data : [];

      // Build monthly maps
      const salesMap = {};
      sales.forEach((row) => {
        if (!row.date) return;
        const d = new Date(row.date);
        if (Number.isNaN(d.getTime())) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        salesMap[key] = (salesMap[key] || 0) + (Number(row.total_amount) || 0);
      });
      const expMap = {};
      expenses.forEach((e) => {
        const ds = e.expense_date;
        if (!ds) return;
        const d = new Date(ds);
        if (Number.isNaN(d.getTime())) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        expMap[key] = (expMap[key] || 0) + (Number(e.amount) || 0);
      });

      // Build series in chronological order
      const months = [];
      for (let i = salesMonths - 1; i >= 0; i--) {
        const dt = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
        const label = `${dt.toLocaleString("th-TH", { month: "short" })} ${String(dt.getFullYear()).slice(-2)}`;
        months.push({ label, sales: salesMap[key] || 0, expenses: expMap[key] || 0 });
      }
      setMonthlySales(months);
    } catch (e) {
      console.error("load sales chart failed", e);
      setMonthlySales([]);
    }
  };

  // Load analytics (peak hours + weekday distribution) based on peakMonths (use larger of peak/weekday for single fetch)
  const loadAnalyticsRange = async () => {
    try {
      const months = Math.max(peakMonths, weekdayMonths);
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth() - (months - 1), 1);
      const res = await api.get('/reports/analytics', { params: { date_from: toIsoDate(start), date_to: toIsoDate(today) } });
      const data = res.data || {};
      setPeakHours(Array.isArray(data.peak_hours) ? data.peak_hours : []);
      // Weekday distribution filtering needs only counts; keep as is
      setOrdersByWeekday(Array.isArray(data.orders_by_weekday) ? data.orders_by_weekday : []);
    } catch (e) {
      console.error('load analytics failed', e);
      setPeakHours([]); setOrdersByWeekday([]);
    }
  };

  // Load monthday distribution
  const loadMonthdayDistribution = async () => {
    try {
      let start, end;
      if (monthdayMode === 'range') {
        const today = new Date();
        start = new Date(today.getFullYear(), today.getMonth() - (monthdayMonths - 1), 1);
        end = today;
      } else {
        start = new Date(monthdayYear, monthdayMonth - 1, 1);
        end = new Date(monthdayYear, monthdayMonth, 0); // last day of month
      }

      const res = await api.get('/reports/analytics', { params: { date_from: toIsoDate(start), date_to: toIsoDate(end) } });
      const data = res.data || {};
      setOrdersByMonthday(Array.isArray(data.orders_by_monthday) ? data.orders_by_monthday : []);
    } catch (e) {
      console.error('load monthday failed', e);
      setOrdersByMonthday([]);
    }
  };

  // Load top items
  const refreshTopItems = useCallback(async (months) => {
    try {
      setTopLoading(true);
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth() - (months - 1), 1);
      const res = await api.get('/reports/top-items', { params: { date_from: toIsoDate(start), date_to: toIsoDate(today), limit: 5 } });
      const data = res.data || {};
      setTopServices(Array.isArray(data.top_services) ? data.top_services : []);
      setTopProducts(Array.isArray(data.top_products) ? data.top_products : []);
    } catch (e) {
      console.error('load top items failed', e);
      setTopServices([]); setTopProducts([]);
    } finally { setTopLoading(false); }
  }, [addNotification]);

  // Progressive Initial Load (ลดแรงกระแทก CPU โดยแบ่งโหลดข้อมูลเป็นระยะ)
  const isInitialMountedRef = useRef(false);

  useEffect(() => {
    // 1. โหลดข้อมูลสรุปส่วนบน (KPI Summary + บิลค้างชำระ) ทันที
    loadSummaryAndDrafts();

    // 2. ทยอยโหลดกราฟยอดขายรายเดือน
    const timerChart = setTimeout(() => {
      loadSalesChart();
    }, 300);

    // 3. ทยอยโหลดสถิติและการวิเคราะห์เชิงลึก
    const timerAnalytics = setTimeout(() => {
      loadAnalyticsRange();
      loadMonthdayDistribution();
    }, 600);

    // 4. ทยอยโหลดสินค้ายอดนิยม
    const timerTopItems = setTimeout(() => {
      refreshTopItems(popularMonths);
    }, 900);

    return () => {
      clearTimeout(timerChart);
      clearTimeout(timerAnalytics);
      clearTimeout(timerTopItems);
    };
  }, []);

  // โหลดเฉพาะวิดเจ็ตที่เปลี่ยนตัวกรอง (ข้ามรอบแรกเพราะมี Progressive Loader แล้ว)
  useEffect(() => {
    if (!isInitialMountedRef.current) return;
    loadSalesChart();
  }, [salesMonths]);

  useEffect(() => {
    if (!isInitialMountedRef.current) return;
    loadAnalyticsRange();
  }, [peakMonths, weekdayMonths]);

  useEffect(() => {
    if (!isInitialMountedRef.current) return;
    loadMonthdayDistribution();
  }, [monthdayMode, monthdayMonths, monthdayYear, monthdayMonth]);

  useEffect(() => {
    if (!isInitialMountedRef.current) {
      isInitialMountedRef.current = true;
      return;
    }
    refreshTopItems(popularMonths);
  }, [popularMonths, refreshTopItems]);

  // Order detail (for unpaid draft interactions)
  const handleViewOrder = async (order) => {
    try {
      setLoadingOrderDetail(true);
      const res = await api.get(`/orders/${order.order_id}`);
      setSelectedOrderDetail(res.data);
      setIsDetailModalOpen(true);
    } catch (e) {
      addNotification('โหลดบิลไม่สำเร็จ', 'danger');
    } finally { setLoadingOrderDetail(false); }
  };
  const handleCloseModal = () => { setIsDetailModalOpen(false); setSelectedOrderDetail(null); };

  // Summary numbers
  const todayRevenue = summary?.revenue_today || 0;
  const monthRevenue = summary?.revenue_month || 0;
  const ordersToday = summary?.orders_today || 0;
  const monthOrders = summary?.orders_month || 0;
  const draftsCount = unpaidOrdersCount;

  // Charts data
  const salesChartData = useMemo(() => ({
    labels: monthlySales.map(m => m.label),
    datasets: [
      { label: 'ยอดขาย (บาท)', data: monthlySales.map(m => m.sales), backgroundColor: '#4e79a7' },
      { label: 'ค่าใช้จ่าย (บาท)', data: monthlySales.map(m => m.expenses), backgroundColor: '#e15759' },
    ]
  }), [monthlySales]);
  const salesChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatMoney(ctx.raw)} ฿`,
        }
      }
    }
  };

  const peakChartData = useMemo(() => ({
    labels: peakHours.map(r => `${String(r.hour).padStart(2, '0')}:00`),
    datasets: [{ label: 'จำนวนบิล', data: peakHours.map(r => r.count), backgroundColor: '#f28e2b' }]
  }), [peakHours]);
  const peakChartOptions = { responsive: true, plugins: { legend: { display: false } } };

  // Weekday reorder Monday-Sunday; strftime('%w'): 0=Sunday..6=Saturday
  const weekdayOrdered = useMemo(() => {
    const map = {}; ordersByWeekday.forEach(w => { map[w.weekday] = w.count; });
    const order = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun
    return order.map(code => ({ code, count: map[code] || 0 }));
  }, [ordersByWeekday]);
  const weekdayChartData = useMemo(() => ({
    labels: weekdayOrdered.map(w => ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา'][w.code]),
    datasets: [{ label: 'จำนวนบิล', data: weekdayOrdered.map(w => w.count), backgroundColor: '#e15759' }]
  }), [weekdayOrdered]);
  const weekdayChartOptions = { responsive: true, plugins: { legend: { display: false } } };

  const monthdayChartData = useMemo(() => ({
    labels: ordersByMonthday.map(d => String(d.day)),
    datasets: [{ label: 'จำนวนบิล', data: ordersByMonthday.map(d => d.count), backgroundColor: '#76b7b2' }]
  }), [ordersByMonthday]);
  const monthdayChartOptions = { responsive: true, plugins: { legend: { display: false } }, scales: { x: { ticks: { maxRotation: 0 } } } };

  return (
    <div className="dashboard-page">
      <div className="dashboard-hero mb-4">
        <div className="d-flex align-items-center gap-3">
          <div className="bg-white p-2 rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="var(--ks-primary)" viewBox="0 0 16 16">
              <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zm8 0A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm-8 8A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm8 0A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3z" />
            </svg>
          </div>
          <div>
            <div className="dashboard-hero-title mb-0">{shopName}</div>
            <div className="dashboard-hero-subtitle">ภาพรวมยอดขายและสถิติร้าน</div>
          </div>
        </div>
      </div>

      {/* สรุปยอด (Summary Cards) */}
      <div className="row row-cols-2 row-cols-sm-3 row-cols-xl-6 g-2 g-md-3 mb-4">
        {[
          {
            label: 'ยอดขายวันนี้',
            value: formatMoney(todayRevenue) + ' ฿',
            sub: 'ยอดชำระแล้ว',
            cls: 'text-success',
            icon: <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1zm3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4h-3.5zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5z" />
          },
          {
            label: 'ออเดอร์วันนี้',
            value: ordersToday,
            sub: 'จำนวนบิล',
            icon: <path d="M2.5 1A1.5 1.5 0 0 0 1 2.5v11A1.5 1.5 0 0 0 2.5 15h6.086a1.5 1.5 0 0 0 1.06-.44l4.915-4.914A1.5 1.5 0 0 0 15 8.586V2.5A1.5 1.5 0 0 0 13.5 1h-11zM2 2.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 .5.5V8H9.5A1.5 1.5 0 0 0 8 9.5V14H2.5a.5.5 0 0 1-.5-.5v-11zm7 11.293V9.5a.5.5 0 0 1 .5-.5h4.293L9 13.793z" />
          },
          {
            label: 'ยอดขายเดือนนี้',
            value: formatMoney(monthRevenue) + ' ฿',
            sub: 'ยอดสะสม',
            icon: <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
          },
          {
            label: 'ค่าใช้จ่ายเดือนนี้',
            value: formatMoney(monthExpenses) + ' ฿',
            sub: 'ยอดสะสม',
            cls: 'text-danger',
            icon: <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
          },
          {
            label: 'ออเดอร์เดือนนี้',
            value: monthOrders,
            sub: 'จำนวนบิล',
            icon: <path d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
          },
          {
            label: 'บิล Draft',
            value: draftsCount,
            sub: 'รอชำระเงิน',
            cls: 'text-danger',
            icon: <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
          }
        ].map((c, i) => (
          <div key={i} className="col">
            <div className="card card-ks-soft h-100 border-0 shadow-sm">
              <div className="card-body p-3 d-flex flex-column justify-content-between">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="card-ks-label mb-0 text-muted">{c.label}</div>
                  <div className={`text-muted opacity-50 ${c.cls ? c.cls.replace('text-', 'text-') : ''}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                      {c.icon}
                    </svg>
                  </div>
                </div>
                <div>
                  <div className={`card-ks-number mb-0 ${c.cls || ''}`} style={{ fontSize: '1.5rem' }}>{c.value}</div>
                  <div className="small text-muted mt-1" style={{ fontSize: '0.75rem' }}>{c.sub}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ยอดขายย้อนหลัง + draft */}
      <div className="row mb-4 g-3">
        <div className="col-lg-7">
          <div className="card card-ks-soft h-100 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0 fw-bold">
                  <i className="bi bi-graph-up me-2"></i>กราฟยอดขายย้อนหลัง
                </h5>
                <div className="btn-group btn-group-sm" role="group">
                  {[3, 6, 12].map(m => (
                    <button
                      key={m}
                      type="button"
                      className={`btn ${salesMonths === m ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      onClick={() => setSalesMonths(m)}
                    >
                      {m} เดือน
                    </button>
                  ))}
                </div>
              </div>
              {monthlySales.length === 0 ? (
                <div className="text-center text-muted py-5">ยังไม่มีข้อมูล</div>
              ) : (
                <div style={{ height: 320 }}>
                  <Bar data={salesChartData} options={{ ...salesChartOptions, maintainAspectRatio: false }} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card card-ks-soft h-100 shadow-sm">
            <div className="card-body d-flex flex-column">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0 fw-bold text-danger">
                  <i className="bi bi-exclamation-circle me-2"></i>บิลรอชำระ (Draft)
                </h5>
                <button type="button" className="btn btn-sm btn-outline-secondary btn-ks-outline" onClick={() => navigate('/pos')}>
                  ไปหน้า POS
                </button>
              </div>

              <div className="flex-grow-1 overflow-auto" style={{ maxHeight: 320 }}>
                {loading && !summary ? (
                  <div className="text-center py-4">กำลังโหลด...</div>
                ) : unpaidOrders.length === 0 ? (
                  <div className="text-center text-muted py-5">ไม่มีบิลค้างชำระ</div>
                ) : (
                  <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>เลขบิล</th>
                        <th>ลูกค้า</th>
                        <th className="text-end">ยอดเงิน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unpaidOrders.slice(0, 10).map(o => (
                        <tr key={o.order_id} style={{ cursor: 'pointer' }} onClick={() => handleViewOrder(o)}>
                          <td><span className="badge bg-light text-dark border">{o.order_code}</span></td>
                          <td><div className="text-truncate" style={{ maxWidth: 100 }}>{o.customer_name || '-'}</div></td>
                          <td className="text-end fw-bold text-danger">{formatMoney(o.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
              {unpaidOrders.length > 10 && (
                <div className="small text-muted text-center mt-2 pt-2 border-top">
                  แสดง 10 รายการล่าสุด (ทั้งหมด {unpaidOrders.length})
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Peak hours + weekday */}
      <div className="row mb-4 g-3">
        <div className="col-lg-7">
          <div className="card card-ks-soft h-100 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0 fw-bold">ช่วงเวลาลูกค้าเยอะ</h5>
                <div className="btn-group btn-group-sm" role="group">
                  {[3, 6, 12].map(m => (
                    <button key={m} type="button" className={`btn ${peakMonths === m ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setPeakMonths(m)}>{m} เดือน</button>
                  ))}
                </div>
              </div>
              {peakHours.length === 0 ? (
                <div className="text-center text-muted py-5">ยังไม่มีข้อมูล</div>
              ) : (
                <div style={{ height: 300 }}>
                  <Bar data={peakChartData} options={{ ...peakChartOptions, maintainAspectRatio: false }} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card card-ks-soft h-100 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0 fw-bold">วันลูกค้าแน่น</h5>
                <div className="btn-group btn-group-sm" role="group">
                  {[3, 6, 12].map(m => (
                    <button key={m} type="button" className={`btn ${weekdayMonths === m ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setWeekdayMonths(m)}>{m} เดือน</button>
                  ))}
                </div>
              </div>
              {weekdayOrdered.every(w => w.count === 0) ? (
                <div className="text-center text-muted py-5">ยังไม่มีข้อมูล</div>
              ) : (
                <div style={{ height: 300 }}>
                  <Bar data={weekdayChartData} options={{ ...weekdayChartOptions, maintainAspectRatio: false }} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Monthday distribution */}
      <div className="card card-ks-soft mb-4 shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <h5 className="card-title mb-0 fw-bold">สถิติรายวันในเดือน</h5>

            <div className="d-flex flex-wrap gap-2 align-items-center bg-light p-1 rounded">
              <div className="btn-group btn-group-sm">
                <button className={`btn ${monthdayMode === 'range' ? 'btn-white shadow-sm fw-bold' : 'btn-light text-muted'}`} onClick={() => setMonthdayMode('range')}>ช่วงเวลา</button>
                <button className={`btn ${monthdayMode === 'month' ? 'btn-white shadow-sm fw-bold' : 'btn-light text-muted'}`} onClick={() => setMonthdayMode('month')}>รายเดือน</button>
              </div>

              <div className="vr mx-1"></div>

              {monthdayMode === 'range' ? (
                <div className="btn-group btn-group-sm">
                  {[3, 6, 12].map(m => (
                    <button key={m} type="button" className={`btn ${monthdayMonths === m ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setMonthdayMonths(m)}>{m} เดือน</button>
                  ))}
                </div>
              ) : (
                <div className="d-flex gap-1">
                  <select className="form-select form-select-sm" style={{ width: 'auto' }} value={monthdayMonth} onChange={e => setMonthdayMonth(Number(e.target.value))}>
                    {['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'].map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 'auto' }} value={monthdayYear} onChange={e => setMonthdayYear(Number(e.target.value))}>
                    {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>

          {ordersByMonthday.length === 0 ? (
            <div className="text-center text-muted py-5">ยังไม่มีข้อมูล</div>
          ) : (
            <div style={{ height: 300 }}>
              <Bar data={monthdayChartData} options={{ ...monthdayChartOptions, maintainAspectRatio: false }} />
            </div>
          )}
        </div>
      </div>

      {/* Popular services/products */}
      <div className="d-flex justify-content-between align-items-end mb-3">
        <div>
          <h4 className="mb-1 fw-bold text-ks-primary">สินค้าและบริการยอดนิยม</h4>
          <p className="text-muted small mb-0">รายการที่มียอดขายสูงสุดในช่วงเวลาที่เลือก</p>
        </div>
        <div className="btn-group btn-group-sm shadow-sm" role="group">
          {[3, 6, 12].map(m => (
            <button key={m} type="button" className={`btn ${popularMonths === m ? 'btn-secondary' : 'btn-outline-secondary bg-white'}`} onClick={() => setPopularMonths(m)}>{m} เดือน</button>
          ))}
        </div>
      </div>

      <div className="row mb-4 g-3">
        <div className="col-lg-6">
          <div className="card card-ks-soft h-100 shadow-sm">
            <div className="card-header bg-white border-bottom-0 pt-3 pb-0">
              <h5 className="card-title mb-0 fw-bold" style={{ color: "#d4af37" }}>บริการยอดนิยม</h5>
            </div>
            <div className="card-body">
              {topLoading ? (
                <div className="text-center py-4">กำลังโหลด...</div>
              ) : topServices.length === 0 ? (
                <div className="text-center text-muted py-5">ยังไม่มีข้อมูล</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '50%' }}>บริการ</th>
                        <th className="text-end" style={{ width: '20%' }}>ครั้ง</th>
                        <th className="text-end" style={{ width: '30%' }}>ยอดขาย</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topServices.map((s, idx) => (
                        <tr key={s.name} style={{ cursor: 'pointer' }} onClick={() => navigate(`/pos?item=${encodeURIComponent(s.name)}&months=${popularMonths}`)}>
                          <td>
                            <span className={`badge me-2 ${idx < 3 ? 'bg-warning text-dark' : 'bg-light text-muted border'}`}>{idx + 1}</span>
                            {s.name}
                          </td>
                          <td className="text-end">{s.qty}</td>
                          <td className="text-end fw-bold">{formatMoney(s.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card card-ks-soft h-100 shadow-sm">
            <div className="card-header bg-white border-bottom-0 pt-3 pb-0">
              <h5 className="card-title mb-0 fw-bold text-success">สินค้ายอดนิยม</h5>
            </div>
            <div className="card-body">
              {topLoading ? (
                <div className="text-center py-4">กำลังโหลด...</div>
              ) : topProducts.length === 0 ? (
                <div className="text-center text-muted py-5">ยังไม่มีข้อมูล</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '50%' }}>สินค้า</th>
                        <th className="text-end" style={{ width: '20%' }}>ชิ้น</th>
                        <th className="text-end" style={{ width: '30%' }}>ยอดขาย</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.map((p, idx) => (
                        <tr key={p.name} style={{ cursor: 'pointer' }} onClick={() => navigate(`/pos?item=${encodeURIComponent(p.name)}&months=${popularMonths}`)}>
                          <td>
                            <span className={`badge me-2 ${idx < 3 ? 'bg-warning text-dark' : 'bg-light text-muted border'}`}>{idx + 1}</span>
                            {p.name}
                          </td>
                          <td className="text-end">{p.qty}</td>
                          <td className="text-end fw-bold">{formatMoney(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <OrderDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseModal}
        orderDetail={selectedOrderDetail}
        loading={loadingOrderDetail}
        payingOrderId={null}
        onPay={null}
        formatMoney={formatMoney}
      />
    </div>
  );
};

export default DashboardPage; // fixed structure
